import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input, Switch } from 'antd'
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ModalButtons } from 'components/reusables/modalButtons'
import { Modal } from 'components/reusables/modal'
import { useAppDispatch } from 'store/hooks'
import { slices } from 'store/slices'
import { IQuestionBankSection } from 'interfaces/questionBank'
import { calculateSectionTotals } from 'helpers/evaluations/calculateSectionTotals'
import {
  OptionWrapper,
  Option,
  Content,
  Title,
  Description,
  Alert,
  Detail,
  DetailContent,
} from './styled'

interface IProps {
  section: IQuestionBankSection
  close: () => void
}

const AdjustQuestions = ({ section, close }: IProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const translationPrefix =
    'administration.blocks.type.questionBankV2.sections.adjustQuestions'
  const [newSection, setNewSection] = useState<IQuestionBankSection>(section)
  const [totalScore, setTotalScore] = useState(0)

  useEffect(() => {
    if (newSection.randomOrder) {
      setTotalScore(
        newSection.randomQuestionsToDisplay * newSection.scoreForEachQuestion
      )
    } else {
      setTotalScore(calculateSectionTotals(newSection).score)
    }
  }, [newSection])

  const save = () => {
    if (newSection.randomOrder) {
      const newQuestions = newSection.questions.map((question) => ({
        ...question,
        score: newSection.scoreForEachQuestion,
      }))
      dispatch(
        slices.questionBank.updateSection({
          section: {
            ...newSection,
            questions: newQuestions,
          },
        })
      )
    } else {
      dispatch(
        slices.questionBank.updateSection({
          section: newSection,
        })
      )
    }
    close()
  }

  const actions = (
    <ModalButtons
      primaryText={t(`${translationPrefix}.buttons.save`)}
      primaryAction={save}
      secondaryText={t(`${translationPrefix}.buttons.cancel`)}
      secondaryAction={close}
    />
  )

  return (
    <Modal
      title={t(`${translationPrefix}.title`)}
      actions={actions}
      maxheight={450}
      maxwidth={800}
      close={close}
    >
      <>
        <OptionWrapper>
          <Option>
            <Content>
              <Title>
                {t(`${translationPrefix}.displayQuestionsInRandomOrder.title`)}
              </Title>
              <Description>
                {t(
                  `${translationPrefix}.displayQuestionsInRandomOrder.description`
                )}
              </Description>
            </Content>
            <Switch
              checked={newSection.randomOrder}
              onChange={(checked) =>
                setNewSection({ ...newSection, randomOrder: checked })
              }
            />
          </Option>
          {newSection.randomOrder && (
            <>
              <Alert>
                <FontAwesomeIcon icon={faCircleInfo} />
                {t(`${translationPrefix}.displayQuestionsInRandomOrder.alert`)}
              </Alert>
              <Detail>
                <DetailContent>
                  <Content>
                    <Title>
                      {t(
                        `${translationPrefix}.displayQuestionsInRandomOrder.numberOfRandomQuestionsToDisplay.title`
                      )}
                    </Title>
                    <Description>
                      {t(
                        `${translationPrefix}.displayQuestionsInRandomOrder.numberOfRandomQuestionsToDisplay.description`
                      )}
                    </Description>
                  </Content>
                  <Input
                    type="number"
                    min={1}
                    value={newSection.randomQuestionsToDisplay}
                    onChange={(e) =>
                      setNewSection({
                        ...newSection,
                        randomQuestionsToDisplay: Number(e.target.value),
                      })
                    }
                    autoFocus
                  />
                </DetailContent>
              </Detail>
              <Option>
                <Content>
                  <Title>
                    {t(
                      `${translationPrefix}.displayQuestionsInRandomOrder.scoreForEachQuestion.title`
                    )}
                  </Title>
                  <Description>
                    {t(
                      `${translationPrefix}.displayQuestionsInRandomOrder.scoreForEachQuestion.description`
                    )}
                  </Description>
                </Content>
                <Input
                  type="number"
                  min={1}
                  value={newSection.scoreForEachQuestion}
                  onChange={(e) =>
                    setNewSection({
                      ...newSection,
                      scoreForEachQuestion: Number(e.target.value),
                    })
                  }
                />
              </Option>
            </>
          )}
        </OptionWrapper>
        <OptionWrapper>
          <Option>
            <Content>
              <Title>{t(`${translationPrefix}.totalScore.title`)}</Title>
              <Description>
                {t(`${translationPrefix}.totalScore.description`)}
              </Description>
            </Content>
            <span>
              {totalScore}
              <span>
                {t(
                  `${translationPrefix}.totalScore.${
                    totalScore === 1 ? 'rowsCount' : 'rowsCountPlural'
                  }`
                )}
              </span>
            </span>
          </Option>
        </OptionWrapper>
      </>
    </Modal>
  )
}

export default AdjustQuestions
